<script setup lang="ts">
// Tela de login da área do afiliado (LoginScreen do template). O header do
// template não entra: a página usa o header real do site, pelo layout afiliado.
import { ArrowUpRight, CircleHelp, Sparkles } from "lucide-vue-next";

const emit = defineEmits<{
  (e: "esqueci", email: string): void;
  (e: "mensagem", texto: string): void;
}>();

const router = useRouter();
const afiliadoApi = useAfiliadoApi();
const { idioma, t } = useAreaAfiliadoTextos();

const email = ref("");
const senha = ref("");
const enviando = ref(false);

async function entrar() {
  if (!email.value.trim() || !senha.value) {
    emit("mensagem", t.value.login.camposObrigatorios);
    return;
  }

  enviando.value = true;
  try {
    await afiliadoApi.login(email.value.trim(), senha.value);
    await router.push({ name: "language-afiliado-painel", params: { language: idioma.value } });
  } catch (erro) {
    senha.value = "";
    emit(
      "mensagem",
      afiliadoApi.statusDe(erro) === 429
        ? t.value.mensagens.muitasTentativas
        : afiliadoApi.mensagemDe(erro) || t.value.mensagens.falhaAoCarregar
    );
  } finally {
    enviando.value = false;
  }
}
</script>

<template>
  <div class="area-afiliado login-page" data-hj-suppress>
    <section class="login-visual">
      <div class="login-visual-top login-visual-top-right">
        <span class="secure-pill"><span class="secure-dot" /> {{ t.login.areaSegura }}</span>
      </div>
      <div class="login-hero-copy">
        <p class="eyebrow eyebrow-light"><Sparkles :size="14" /> {{ t.login.eyebrow }}</p>
        <h1>{{ t.login.titulo }} <em>{{ t.login.tituloDestaque }}</em></h1>
        <p class="login-lede">{{ t.login.lede }}</p>
      </div>
      <div class="login-ornament ornament-one" />
      <div class="login-ornament ornament-two" />
    </section>
    <section class="login-panel">
      <div class="login-panel-inner">
        <span class="section-kicker">{{ t.login.kicker }}</span>
        <h2>{{ t.login.entrarNaConta }}</h2>
        <p class="panel-copy">{{ t.login.copy }}</p>
        <form class="login-form" novalidate @submit.prevent="entrar">
          <label for="afiliado-email">{{ t.login.email }}</label>
          <input
            id="afiliado-email"
            v-model="email"
            type="email"
            autocomplete="username"
            :placeholder="t.login.emailPlaceholder"
            required
          />
          <div class="label-row">
            <label for="afiliado-senha">{{ t.login.senha }}</label>
            <button type="button" class="text-button" @click="emit('esqueci', email.trim())">
              {{ t.login.esqueci }}
            </button>
          </div>
          <input
            id="afiliado-senha"
            v-model="senha"
            type="password"
            autocomplete="current-password"
            placeholder="••••••••"
            required
          />
          <button class="primary-button login-button" type="submit" :disabled="enviando">
            {{ t.login.entrar }} <ArrowUpRight :size="16" />
          </button>
        </form>
        <div class="login-divider"><span>{{ t.login.ou }}</span></div>
        <NuxtLink class="outline-button support-button" :to="`/${idioma}/programa-afiliados`">
          <CircleHelp :size="16" /> {{ t.login.naoSouAfiliado }}
        </NuxtLink>
        <p class="login-note">{{ t.login.nota }}</p>
      </div>
    </section>
  </div>
</template>

<style src="./area-afiliado.css"></style>
